/**
 * @fileoverview A React component that renders a phone validation code input field.
 * Provides a 4-digit code input interface where each digit is entered in a separate input box.
 * Supports automatic focus advancement, backspace navigation, and error states.
 *
 * @example
 * <PhoneValidationRenderer
 *   data=""
 *   path="phone.code"
 *   handleChange={(path, value) => console.log(path, value)}
 *   errors={[]}
 * />
 */


import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useRef, useState } from 'react'
import css from './PhoneValidationRenderer.module.css'

/**
 * @interface PhoneValidationProps
 * @description Interface for the PhoneValidationRenderer component.
 * @property {string} data - The data for the phone validation.
 * @property {string} label - The label for the phone validation.
 * @property {boolean} required - Whether the phone validation is required.
 * @property {function} handleChange - The function to handle the change of the phone validation.
 * @property {string} path - The path of the phone validation.
 */
interface PhoneValidationProps extends ControlProps {
    data: string
    label: string
    required?: boolean
    handleChange(path: string, value: string): void
    path: string
}

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function PhoneValidationRenderer
 * @description Component for rendering a phone validation code input field.
 * @param {PhoneValidationProps} props - The props for the PhoneValidationRenderer component.
 * @returns {JSX.Element} The PhoneValidationRenderer component.
 */
function PhoneValidationRenderer({ errors, data = '', handleChange, path }: PhoneValidationProps) {
    const [code, setCode] = useState<string[]>(data.split(''))
    const hasError = errors.length > 0
    const inputsRef = useRef<(HTMLInputElement | null)[]>([])

    const handleInputChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = [...code]
        newCode[index] = event.target.value
        if (newCode[index].length <= 1 && /^[0-9]*$/.test(newCode[index])) {
            setCode(newCode)
            handleChange(path, newCode.join(''))
            if (index < 3 && newCode[index] !== '') {
                inputsRef.current[index + 1]?.focus()
            }
        }
    }

    const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && event.currentTarget.value === '') {
            if (index > 0) {
                inputsRef.current[index - 1]?.focus()
            }
        }
    }

    return (
        <>
            <div className={classNames(css.container, hasError ? css.error : null)}>
                {Array.from({ length: 4 }, (_, index) => (
                    <input
                        key={index}
                        type="number"
                        value={code[index] || ''}
                        className={css.input}
                        autoComplete="one-time-code"
                        onChange={handleInputChange(index)}
                        onKeyDown={handleKeyDown(index)}
                        maxLength={1}
                        ref={(ref) => (inputsRef.current[index] = ref)}
                    />
                ))}
            </div>
            {hasError && <span className={css.errorMessage}>{errors}</span>}
        </>
    )
}

export const PhoneValidationTester = rankWith(3, uiTypeIs('PhoneValidation'))

export default withJsonFormsControlProps(PhoneValidationRenderer)
